import { Injectable } from '@angular/core';
import {
  MembersService,
  OrganisationsService,
  SessionsService,
  TenantsService,
} from '@careapp-group/ng-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import {
  forkJoin as observableForkJoin,
  from,
  from as observableFrom,
  Observable,
  of as observableOf,
  of,
} from 'rxjs';
import { switchMap, withLatestFrom, map, catchError, tap } from 'rxjs/operators';
import * as actions from '../actions';
import { FORCE_LOGOUT_401, SessionReadyAction } from '../actions';
import * as selectors from '../reducers';
import { auth } from '../auth/auth.selectors';
import { FrontendApi, Session } from '@ory/client';
import { differenceInMilliseconds, parseISO } from 'date-fns';
import { Ability } from '@casl/ability';
import { Router } from '@angular/router';

export const NO_ORGANISATIONS = 'NO_ORGANISATIONS';
export const NO_USER = 'NO_USER';
export const NO_ROLES = 'NO_ROLES';
export const FAIL_TOKEN_REFRESH = 'FAIL_TOKEN_REFRESH';
const DAY_MS = 1000 * 60 * 60 * 24;

@Injectable()
export class KratosCoreEffects {
  applicationStartup = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RESPONSE_REHYDRATED),
      map(() => actions.PerformSessionCheck({ reason: actions.SessionCheckReason.appStartup }))
    )
  );

  checkSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.PerformSessionCheck),
      switchMap((action) =>
        from(this.kratos.toSession()).pipe(
          map((session) => {
            if (session?.data?.id && session?.data?.active) {
              return SessionReadyAction({
                session: session.data,
                userId: session.data.identity?.metadata_public['ca_member_id'],
                reason: action.reason,
              });
            } else {
              return actions.ApplicationReady();
            }
          }),
          catchError((err) => {
            if (err.response.data.error.id === 'session_aal2_required') {
              return of(
                actions.NavigateAction({
                  path: ['/', 'auth'],
                  query: { aal: 'aal2' },
                  reason: 'session_aal2_required',
                  extras: {
                    replaceUrl: true,
                  },
                })
              );
            } else {
              return of(actions.ApplicationReady());
            }
          })
        )
      ),
      catchError((err) => of(actions.ApplicationReady()))
    )
  );

  // Gets the current session from Kratos and runs through
  // an auth flow like Login just happened. Keeps the user
  // logged in after password reset.
  authRefresh = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RefreshAppStartup),
      map(() => actions.PerformSessionCheck({ reason: actions.SessionCheckReason.afterRecovery }))
    )
  );

  fetchUserAfterRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.SuccessCompleteRegistrationFlow),
      switchMap((action) =>
        this.membersService
          .getMember({ id: action.response.identity.metadata_public['ca_member_id'] })
          .pipe(
            map(
              (response) => actions.ResponseSuccessLoggedInUser({ payload: response }),
              catchError((err) => observableOf(actions.ResponseFailLoggedInUser(err)))
            )
          )
      )
    )
  );

  refreshAfterAcceptingInvite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.SuccessAcceptInvite),
      withLatestFrom(this.store.select(auth.getAuth)),
      switchMap(
        ([action, auth]): Observable<any> =>
          this.performStartupFetches(auth.userId).pipe(
            switchMap((results) =>
              observableFrom([
                ...results,
                actions.NavigateAction({
                  path: ['/'],
                }),
              ])
            )
          )
      )
    )
  );

  setAbility$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.ResponseSuccessFetchAbility),
        tap((rules) => {
          this.ability.update(rules.ability);
        })
      ),
    {
      dispatch: false,
    }
  );

  checkSessionAfterLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.SuccessUpdateLoginFlow),
      map(() => actions.PerformSessionCheck({ reason: actions.SessionCheckReason.afterLogin }))
    )
  );

  haveSession = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.SessionReadyAction),
      withLatestFrom(
        this.store.select(auth.getAuth),
        this.store.select(selectors.getSelectedId('organisations')),
        this.store.select(auth.getRoles),
        this.store.select(auth.getLoggedInUser)
      ),
      switchMap(([action, auth, selectedOrgId, roles, user]): Observable<any> => {
        return this.extendSessionIfRequired(auth.session).pipe(
          switchMap((updatedTokenAction) =>
            this.performStartupFetches(auth.userId).pipe(
              map((results) => {
                if (
                  action.reason === actions.SessionCheckReason.afterLogin ||
                  action.reason === actions.SessionCheckReason.afterRecovery
                ) {
                  return [...results, actions.ApplicationLoginComplete()];
                }
                return results;
              }),
              switchMap((results) =>
                this.createObservableStreamFromResults(results, updatedTokenAction, selectedOrgId)
              )
            )
          )
        );
      })
    )
  );

  clearOnLogout = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.ResponseSuccessAuthLogoutAction),
      map((action) => new actions.RequestStateReset())
    )
  );

  constructor(
    private actions$: Actions,
    private membersService: MembersService,
    private orgsService: OrganisationsService,
    private sessionsService: SessionsService,
    private tenantsService: TenantsService,
    private store: Store<any>,
    private kratos: FrontendApi,
    private ability: Ability
  ) {}

  createObservableStreamFromResults(
    results: any[],
    updatedTokenAction: any,
    selectedOrgId: string
  ) {
    const failure = results.find((res) => res.type === FORCE_LOGOUT_401);
    let actionStream: any[] = results.concat([updatedTokenAction, actions.ApplicationReady()]);
    const [memberResults, roleResults, orgResults, ability] = results;
    if (failure) {
      return observableFrom(actionStream);
    }
    // If the user has any global role, then they are allowed to log in
    if (!roleResults.payload.length) {
      // Filter orgs based on user permissions (exclude non dashboard roles)
      const authorizedOrgs = orgResults.payload;
    }
    const orgId = selectedOrgId || orgResults.payload[0]?.id;

    actionStream = actionStream.concat(
      actions.ApplicationAuthorized({
        user: memberResults.payload,
        organisationId: orgId,
        roles: roleResults.payload,
      })
    );

    return observableFrom(actionStream);
  }

  performStartupFetches(userId: string) {
    return observableForkJoin([
      this.membersService.getMember({ id: userId }).pipe(
        map((updatedUser: any) => actions.ResponseSuccessLoggedInUser({ payload: updatedUser })),
        catchError((err) => observableOf(actions.ForceLogout401({ payload: NO_USER })))
      ),
      this.membersService.getMemberRoles({ id: userId }).pipe(
        map((updatedRoles: string[]) =>
          actions.ResponseSuccessUserRoles({ payload: updatedRoles })
        ),
        catchError((err) => observableOf(actions.ForceLogout401({ payload: NO_ROLES })))
      ),
      this.tenantsService.getTenants({}).pipe(
        map((tenants) => actions.Tenants.ResponseSuccessRetrieveAllAction({ payload: tenants })),
        catchError((err) => {
          return observableOf(actions.Organisations.ResponseFailAllAction(err));
        })
      ),
      this.orgsService.listOrganisations().pipe(
        map((organisations) => actions.Organisations.ResponseSuccessOwn(organisations)),
        catchError((err) => {
          return observableOf(actions.Organisations.ResponseFailAllAction(err));
        })
      ),
      this.membersService.getAbility({ id: userId }).pipe(
        map((response) => actions.ResponseSuccessFetchAbility({ ability: response })),
        catchError((err) => observableOf(actions.ForceLogout401({ payload: NO_ROLES })))
      ),
    ]);
  }

  extendSessionIfRequired(session: Session) {
    // TODO Replace this with new refresh endpoint
    let msSinceCreated;
    if (session) {
      msSinceCreated = differenceInMilliseconds(new Date(), parseISO(session.authenticated_at));
    } else {
      return observableOf(actions.ForceLogout401({ payload: FAIL_TOKEN_REFRESH }));
    }
    if (msSinceCreated > DAY_MS) {
      return this.sessionsService.refreshSession().pipe(
        map(() => {
          return actions.ResponseSuccessSessionRefreshAction({});
        }),
        catchError((ex) => {
          if (ex.statusCode === 401) {
            return observableFrom([
              actions.ResponseFailAuthRefreshAction(ex),
              actions.ForceLogout401({ payload: FAIL_TOKEN_REFRESH }),
            ]);
          } else {
            // Failed to refresh for other reason - use stale token.
            return observableOf(actions.ResponseFailAuthRefreshAction({ payload: auth }));
          }
        })
      );
    } else {
      return observableOf(actions.ResponseSuccessSessionRefreshAction({}));
    }
  }
}
